import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Users, Edit, Trash2, Phone, User, Calendar, FileText, ArrowLeft, UserCog } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const Customers = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [salesmen, setSalesmen] = useState([]);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    address: '',
    email: '',
    rm_id: '',
  });

  useEffect(() => {
    loadCustomers();
    loadSalesmen();
  }, []);

  const loadSalesmen = async () => {
    try {
      const roles = ['salesman', 'stock_supervisor', 'admin', 'owner'];
      const results = await Promise.all(roles.map(r => api.get(`/users/by-role/${r}`).catch(() => ({ data: [] }))));
      const merged = results.flatMap(r => r.data);
      // Deduplicate by id and sort by username
      const unique = Object.values(Object.fromEntries(merged.map(u => [u.id, u])));
      unique.sort((a, b) => a.username.localeCompare(b.username));
      setSalesmen(unique);
    } catch (error) {
      console.error('Error loading salesmen:', error);
    }
  };

  const loadCustomers = async (q = '') => {
    try {
      setLoading(true);
      const url = q ? `/crm/clients?q=${encodeURIComponent(q)}` : '/crm/clients';
      const res = await api.get(url);
      const normalised = res.data.map(c => ({
        customer_phone: c.client_phone,
        customer_name:  c.client_name,
        session_count:  c.total_sessions,
        rm_name:        c.rm_name || null,
        rm_id:          c.rm_id  || null,
        won_leads:      c.won_leads,
        lost_leads:     c.lost_leads,
        total_visits:   c.total_visits,
        last_activity:  c.last_activity,
      }));      setCustomers(normalised);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => loadCustomers(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setEditForm({
      name: customer.client_name || '',
      phone: customer.client_phone || '',
      address: customer.address || '',
      email: customer.email || '',
      rm_id: customer.rm_id || '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      // Update RM on all sessions for this client (admin/crm only)
      if (['admin', 'crm', 'owner'].includes(currentUser?.role) && editForm.rm_id && editForm.rm_id !== selectedCustomer.rm_id) {
        await api.patch(`/crm/clients/${encodeURIComponent(selectedCustomer.client_phone)}/rm?rm_id=${editForm.rm_id}`);
      }
      toast.success('Customer updated successfully');
      setEditDialogOpen(false);
      loadCustomers();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-emerald-600 text-white p-3 shadow-md">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-emerald-700 flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <Users className="h-5 w-5 flex-shrink-0" />
              <h1 className="text-base sm:text-xl font-bold truncate">Customer Management</h1>
            </div>
          </div>
          <div className="text-sm flex-shrink-0">
            {customers.length} Customer{customers.length !== 1 ? 's' : ''}
          </div>
        </div>
        {/* Search bar */}
        <div className="max-w-7xl mx-auto mt-2">
          <Input
            placeholder="Search by name or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-white/90 text-gray-800 placeholder-gray-400 border-0 h-9"
            data-testid="customer-search-input"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4">
        {loading && customers.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading customers...</p>
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No customers found</p>
            <p className="text-gray-500 text-sm mt-2">
              {search ? `No results for "${search}"` : 'Customers will appear here once sessions are created'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Customer Name</TableHead>
                  <TableHead className="font-semibold">Phone Number</TableHead>
                  <TableHead className="font-semibold">Relationship Manager</TableHead>
                  <TableHead className="font-semibold text-center">Sessions</TableHead>
                  <TableHead className="font-semibold">Last Activity</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer, index) => (
                  <TableRow
                    key={index}
                    className="hover:bg-emerald-50 cursor-pointer"
                    onClick={() => navigate(`/client-timeline/${customer.customer_phone}`)}
                    data-testid={`customer-row-${index}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-emerald-600" />
                        </div>
                        <span className="font-medium">{customer.customer_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone className="w-4 h-4 text-gray-400" />
                        {customer.customer_phone}
                      </div>
                    </TableCell>
                    <TableCell>
                      {customer.rm_name ? (
                        <div className="flex items-center gap-2">
                          <UserCog className="w-4 h-4 text-blue-500" />
                          <span className="text-sm text-gray-700">{customer.rm_name}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not assigned</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                        <FileText className="w-3 h-3" />
                        {customer.session_count}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(customer.last_activity)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={e => { e.stopPropagation(); handleEdit(customer); }}
                        data-testid={`edit-customer-${index}`}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Customer Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Customer Name *</Label>
              <Input
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Enter customer name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="Enter phone number"
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500">Phone number cannot be changed</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="Enter email address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <textarea
                id="address"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                placeholder="Enter customer address"
                rows={3}
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>

            {/* RM Selection - Admin Only */}
            <div className="space-y-2">
              <Label htmlFor="rm">
                <div className="flex items-center gap-2">
                  <UserCog className="w-4 h-4 text-blue-500" />
                  Relationship Manager
                </div>
              </Label>
              {currentUser?.role === 'admin' ? (
                <Select value={editForm.rm_id} onValueChange={(value) => setEditForm({ ...editForm, rm_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select RM (Salesman)" />
                  </SelectTrigger>
                  <SelectContent>
                    {salesmen.map((salesman) => (
                      <SelectItem key={salesman.id} value={salesman.id}>
                        {salesman.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={selectedCustomer?.rm_name || 'Not assigned'}
                  disabled
                  className="bg-gray-50"
                />
              )}
              <p className="text-xs text-gray-500">
                {currentUser?.role === 'admin'
                  ? 'Select the salesman responsible for this customer'
                  : 'Only admin can change Relationship Manager'
                }
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={loading || !editForm.name}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
